<template>
  <BottomSheetModal v-model="visible" hide-confirm auto-height>
    <template #title>{{ $t('phonePrompt.title') }}</template>

    <div class="pp-wrap">
      <!-- Step 1: enter phone number -->
      <template v-if="step === 'phone'">
        <div class="pp-icon">
          <v-icon size="40" color="brand">mdi-cellphone-text</v-icon>
        </div>

        <p class="pp-explain">{{ $t('phonePrompt.explain') }}</p>

        <IosSection form>
          <div class="acc-phone-row acc-phone-row--first">
            <button class="acc-phone-country" @click="showCountryPicker = true">
              {{ selectedCountryCode.flag }} {{ selectedCountryCode.code }}
              <v-icon size="13" style="opacity:0.5">mdi-chevron-down</v-icon>
            </button>
            <input
              v-model="phone"
              type="tel"
              inputmode="tel"
              autocomplete="tel-national"
              class="acc-phone-input"
              :placeholder="$t('account.phoneNumber')"
            />
          </div>
        </IosSection>

        <div v-if="errorMsg" class="pp-error">{{ errorMsg }}</div>

        <button
          class="pp-primary-btn"
          :disabled="!isValid || saving"
          @click="submitPhone"
        >
          <v-progress-circular v-if="saving" indeterminate size="18" width="2" color="white" />
          <span v-else>{{ $t('phonePrompt.savePhone') }}</span>
        </button>

        <button class="pp-secondary-btn" :disabled="saving" @click="dismiss">
          {{ $t('phonePrompt.notNow') }}
        </button>
      </template>

      <!-- Step 2: verify SMS code -->
      <template v-else>
        <div class="pp-icon">
          <v-icon size="40" color="warning">mdi-cellphone-message</v-icon>
        </div>

        <p class="pp-explain">
          {{ $t('account.verificationCodeSent', { phone: pendingPhone }) }}
        </p>

        <IosSection form>
          <IosInput
            v-model="code"
            :placeholder="$t('account.enterVerificationCode')"
            inputmode="numeric"
            :maxlength="6"
            first
            last
          />
        </IosSection>

        <div v-if="errorMsg" class="pp-error">{{ errorMsg }}</div>

        <button
          class="pp-primary-btn"
          :disabled="code.length !== 6 || saving"
          @click="confirmCode"
        >
          <v-progress-circular v-if="saving" indeterminate size="18" width="2" color="white" />
          <span v-else>{{ $t('account.confirmChange') }}</span>
        </button>

        <button class="pp-secondary-btn" :disabled="saving" @click="step = 'phone'">
          {{ $t('phonePrompt.changeNumber') }}
        </button>
      </template>
    </div>

    <!-- Country code picker (nested) -->
    <BottomSheetModal v-model="showCountryPicker" hide-confirm auto-height>
      <template #title>{{ $t('account.phonePrefix') }}</template>
      <div style="padding: 8px 20px 20px">
        <IosSection form>
          <IosRow
            v-for="(cc, index) in countryCodes"
            :key="cc.code"
            :label="`${cc.flag}  ${cc.name}`"
            :first="index === 0"
            :last="index === countryCodes.length - 1"
            clickable
            @click="selectedCountryCode = cc; showCountryPicker = false"
          >
            <template #value>
              <div class="d-flex align-center" style="gap: 8px">
                <span style="opacity: 0.6">{{ cc.code }}</span>
                <v-icon v-if="selectedCountryCode.code === cc.code" size="20" color="success">mdi-check</v-icon>
              </div>
            </template>
          </IosRow>
        </IosSection>
      </div>
    </BottomSheetModal>
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import axiosInstance from '@/services/axiosInstance'
import { useUserStore } from '@/stores/user/userStore'
import { usePhonePromptStore } from '@/stores/phonePrompt/phonePromptStore'
import BottomSheetModal from '@/components/BottomSheetModal.vue'
import { IosSection, IosRow, IosInput } from '@/components/ios'

const { t } = useI18n()
const userStore = useUserStore()
const phonePromptStore = usePhonePromptStore()
const { visible } = storeToRefs(phonePromptStore)

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'

type CountryCode = { code: string; name: string; flag: string }
const countryCodes = computed<CountryCode[]>(() => [
  { code: '+420', name: t('countries.czechRepublic'), flag: '🇨🇿' },
  { code: '+421', name: t('countries.slovakia'), flag: '🇸🇰' },
  { code: '+48', name: t('countries.poland'), flag: '🇵🇱' },
  { code: '+49', name: t('countries.germany'), flag: '🇩🇪' },
  { code: '+43', name: t('countries.austria'), flag: '🇦🇹' },
  { code: '+41', name: t('countries.switzerland'), flag: '🇨🇭' },
  { code: '+36', name: t('countries.hungary'), flag: '🇭🇺' },
  { code: '+44', name: t('countries.unitedKingdom'), flag: '🇬🇧' },
  { code: '+33', name: t('countries.france'), flag: '🇫🇷' },
  { code: '+39', name: t('countries.italy'), flag: '🇮🇹' },
  { code: '+34', name: t('countries.spain'), flag: '🇪🇸' },
  { code: '+31', name: t('countries.netherlands'), flag: '🇳🇱' },
  { code: '+32', name: t('countries.belgium'), flag: '🇧🇪' },
  { code: '+46', name: t('countries.sweden'), flag: '🇸🇪' },
  { code: '+47', name: t('countries.norway'), flag: '🇳🇴' },
  { code: '+45', name: t('countries.denmark'), flag: '🇩🇰' },
  { code: '+358', name: t('countries.finland'), flag: '🇫🇮' },
  { code: '+1', name: t('countries.usaCanada'), flag: '🇺🇸' },
])

const selectedCountryCode = ref<CountryCode>(countryCodes.value[0])
const showCountryPicker = ref(false)

const phone = ref('')
const code = ref('')
const step = ref<'phone' | 'verify'>('phone')
const saving = ref(false)
const errorMsg = ref<string | null>(null)
const pendingPhone = ref('')

const isValid = computed(() => {
  const digits = phone.value.replace(/\D/g, '')
  return digits.length >= 9 && digits.length <= 15
})

watch(visible, (open) => {
  if (open) {
    // reset on open
    phone.value = ''
    code.value = ''
    step.value = 'phone'
    saving.value = false
    errorMsg.value = null
    pendingPhone.value = ''
    selectedCountryCode.value = countryCodes.value[0]
  }
})

async function submitPhone() {
  if (!isValid.value || !userStore.user?._id) return
  saving.value = true
  errorMsg.value = null
  try {
    const digits = phone.value.replace(/\D/g, '')
    const codeDigits = selectedCountryCode.value.code.replace(/\D/g, '')
    const fullPhone = codeDigits + digits

    const { data } = await axiosInstance.patch(
      `${API_BASE_URL}/users/${userStore.user._id}`,
      { phone: fullPhone, skipSmsVerification: true },
    )

    if (data?.phonePending && data?.pendingPhone) {
      pendingPhone.value = data.pendingPhone
      step.value = 'verify'
      await userStore.fetchUserData()
    } else {
      // Phone saved without verification – done
      await userStore.fetchUserData()
      phonePromptStore.close()
    }
  } catch (err: any) {
    console.error('phonePrompt submitPhone error:', err)
    errorMsg.value = err?.response?.data?.message ?? t('phonePrompt.saveError')
  } finally {
    saving.value = false
  }
}

async function confirmCode() {
  if (code.value.length !== 6) return
  saving.value = true
  errorMsg.value = null
  try {
    await axiosInstance.post(`${API_BASE_URL}/users/confirm-phone-change`, {
      code: code.value.trim(),
    })
    await userStore.fetchUserData()
    phonePromptStore.close()
  } catch (err: any) {
    console.error('phonePrompt confirmCode error:', err)
    errorMsg.value = err?.response?.data?.message ?? t('account.phoneCodeError')
  } finally {
    saving.value = false
  }
}

function dismiss() {
  phonePromptStore.close()
}
</script>

<style lang="scss" scoped>
.pp-wrap {
  padding: 8px 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.pp-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}

.pp-explain {
  font-size: 0.92rem;
  line-height: 1.5;
  text-align: center;
  opacity: 0.78;
  margin: 0 0 18px;
  padding: 0 4px;
}

.pp-error {
  margin-top: 12px;
  padding: 10px 14px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #d32f2f;
  border-radius: 10px;
  font-size: 0.85rem;
  text-align: center;
}

.pp-primary-btn {
  margin-top: 18px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgb(var(--v-theme-brand));
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.18s ease;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.pp-secondary-btn {
  margin-top: 10px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  background: transparent;
  color: rgba(var(--v-theme-sheetText), 0.7);
  font-weight: 500;
  font-size: 0.92rem;
  border: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

/* Reuse styles compatible with account.vue phone row */
.acc-phone-row {
  display: flex;
  align-items: stretch;
  padding: 0;

  &--first {
    /* keep IosSection rounded look */
  }
}

.acc-phone-country {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 14px;
  background: transparent;
  border: none;
  border-right: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));
  cursor: pointer;
  white-space: nowrap;
}

.acc-phone-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 12px 14px;
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));

  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.4);
  }
}
</style>
